import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  darken,
  lighten,
} from "@mui/material";
import {
  ArrowForwardRounded,
  LockOutlined,
  NorthEastRounded,
  StarBorderRounded,
  StarRounded,
} from "@mui/icons-material";
import color from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { DashboardModuleDefinition, ModuleTone } from "./dashboardModules";
import { DashboardColors, useDashboardColors } from "./dashboardColors";

// Stars stay gold whatever the workspace accent is (some are yellow on white).
const FAVORITE_COLOR = color.orange500;
// Fixed for the same reason: the unlock link must stay readable.
const UNLOCK_COLOR = color.orange600;

/** The three header looks, so a grid of cards is not one flat colour. */
const headerBackground = (
  tone: ModuleTone,
  primary: string,
  colors: DashboardColors,
) => {
  const { accent } = colors;
  if (tone === 1) {
    return `radial-gradient(circle at 100% 0%, ${alpha(primary, 0.35)}, transparent 60%), linear-gradient(135deg, ${darken(accent, 0.12)}, ${lighten(accent, 0.15)})`;
  }
  if (tone === 2) {
    return `linear-gradient(120deg, ${darken(primary, 0.2)} 0%, ${primary} 35%, ${darken(accent, 0.05)} 100%)`;
  }
  return `radial-gradient(circle at 100% 0%, ${alpha(accent, 0.55)}, transparent 55%), linear-gradient(135deg, ${darken(primary, 0.2)}, ${lighten(primary, 0.12)})`;
};

interface ModuleCardProps {
  module: DashboardModuleDefinition;
  isFavorite: boolean;
  lastOpened: string | null;
  onOpen: () => void;
  onToggleFavorite: () => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
  module,
  isFavorite,
  lastOpened,
  onOpen,
  onToggleFavorite,
}) => {
  const { t } = useTranslation("home");
  const { colors: workspace } = useWorkspaceColors();
  const dashboardColors = useDashboardColors();
  const { accent, accentIcon, accentBorder } = dashboardColors;
  const onAccentHeader = module.tone === 1;
  const Icon = module.icon;
  const locked = !module.hasAccess;

  const footer = locked
    ? t("hub.modules.unlock")
    : module.externalUrl
      ? t("hub.modules.external")
      : lastOpened
        ? t("hub.modules.openedAgo", { time: lastOpened })
        : t("hub.modules.notOpened");

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  };

  const favoriteLabel = isFavorite
    ? t("hub.modules.removeFavorite")
    : t("hub.modules.addFavorite");

  return (
    <Box
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      aria-label={`${module.title}${locked ? ` – ${t("hub.modules.locked")}` : ""}`}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: 196,
        overflow: "hidden",
        borderRadius: "16px",
        border: `1px solid ${color.gray200}`,
        backgroundColor: color.white,
        cursor: "pointer",
        transition:
          "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: accentBorder,
          boxShadow: `0 12px 28px ${alpha(accent, 0.18)}`,
        },
        "&:hover .module-arrow": { transform: "translateX(3px)" },
        "&:focus-visible": {
          outline: `2px solid ${workspace.primary_dark}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 72,
          flexShrink: 0,
          background: locked
            ? `linear-gradient(135deg, ${color.gray300}, ${color.gray200})`
            : // Workspace secondary colours vary too much (white, neon) to
              // share a gradient, so headers mix only primary and accent.
              headerBackground(
                module.tone,
                workspace.primary_dark,
                dashboardColors,
              ),
        }}
      >
        <Icon
          aria-hidden
          sx={{
            position: "absolute",
            right: -10,
            bottom: -26,
            fontSize: 112,
            color: alpha(
              color.white,
              locked ? 0.35 : onAccentHeader ? 0.24 : 0.14,
            ),
            transform: "rotate(-8deg)",
          }}
        />
        {locked && (
          <Box
            component="span"
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 600,
              lineHeight: "16px",
              color: color.gray600,
              backgroundColor: color.white,
            }}
          >
            <LockOutlined sx={{ fontSize: 13 }} />
            {t("hub.modules.locked")}
          </Box>
        )}
        {!locked && (
          <Tooltip title={favoriteLabel} arrow>
            <IconButton
              size="small"
              aria-label={favoriteLabel}
              aria-pressed={isFavorite}
              onClick={(event) => {
                event.stopPropagation();
                onToggleFavorite();
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: isFavorite ? FAVORITE_COLOR : color.white,
                backgroundColor: isFavorite
                  ? color.white
                  : onAccentHeader
                    ? alpha(color.black, 0.18)
                    : alpha(color.white, 0.12),
                "&:hover": {
                  backgroundColor: isFavorite
                    ? color.white
                    : onAccentHeader
                      ? alpha(color.black, 0.28)
                      : alpha(color.white, 0.24),
                },
              }}
            >
              {isFavorite ? (
                <StarRounded sx={{ fontSize: 20 }} />
              ) : (
                <StarBorderRounded sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
        <Box
          sx={{
            position: "absolute",
            left: 16,
            bottom: -20,
            width: 44,
            height: 44,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: color.white,
            border: `1px solid ${color.gray200}`,
            boxShadow: "0 4px 10px rgba(3, 46, 77, 0.10)",
            color: locked
              ? color.gray400
              : onAccentHeader
                ? workspace.primary_dark
                : accentIcon,
          }}
        >
          <Icon sx={{ fontSize: 24 }} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, px: 2, pt: 3.75, pb: 1.5, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            lineHeight: "20px",
            color: locked ? color.gray500 : color.primary_dark,
          }}
        >
          {module.title}
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            fontSize: 13,
            lineHeight: "18px",
            color: color.gray500,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {module.description}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderTop: `1px solid ${color.gray100}`,
        }}
      >
        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            lineHeight: "16px",
            color: locked ? UNLOCK_COLOR : color.gray500,
            fontWeight: locked ? 600 : 400,
          }}
        >
          {footer}
        </Typography>
        {locked || module.externalUrl ? (
          <NorthEastRounded
            className="module-arrow"
            sx={{
              fontSize: 18,
              color: locked ? UNLOCK_COLOR : accentIcon,
              transition: "transform 0.15s ease",
            }}
          />
        ) : (
          <ArrowForwardRounded
            className="module-arrow"
            sx={{
              fontSize: 18,
              color: accentIcon,
              transition: "transform 0.15s ease",
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ModuleCard;
